/**
 * PageLoadingScaffold — Unified loading affect for LN Pro pages.
 *
 * Renders:
 *   - Top: <LinearProgress /> under the navbar when loading || refreshing (debounced 150ms)
 *   - Body: Skeleton scaffold (variant-driven) when loading=true (no debounce — visual continuity)
 *   - Children: rendered when loading=false
 *
 * Reference impl: frontend/src/components/PropertiesPage.tsx lines 2818-2821 + 3041-3060
 *
 * Two ways to use:
 *   (1) <PageLoadingScaffold loading=...>...</PageLoadingScaffold>
 *       — primitive renders top-bar + scaffold + your children at its position.
 *   (2) <LoadingTopBar /> at page header + <PageLoadingScaffold> at body
 *       — when your page header is far above the scaffold position (e.g. PropertiesPage,
 *       AdminDashboard with mid-layout content like search bars or stats rows).
 *
 * Authored 2026-04-25 by Agent 25 (mc, Worker #16) per Grant v-paste #4
 *   `[V- LN PRO · UNIFIED LOADING PRIMITIVE LANE]` and PLAN
 *   `shared/PLAN-unified-loading-scaffold-agent-25.md`.
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Skeleton,
} from "@mui/material";

export type PageLoadingVariant =
  | "card-grid"
  | "list"
  | "dashboard"
  | "detail";

export interface PageLoadingScaffoldProps {
  /** Page-level loading state. Drives both the top bar and the scaffold render. */
  loading: boolean;
  /** Background refetch state. Drives the top bar only (scaffold stays hidden). */
  refreshing?: boolean;
  /** Scaffold shape. Defaults to "card-grid" (the PropertiesPage look). */
  variant?: PageLoadingVariant;
  /** Row count for "card-grid" + "list". Defaults to 6. */
  rows?: number;
  /** Render the fixed top progress bar. Defaults to true. */
  showTopBar?: boolean;
  /** Page content rendered when loading=false. */
  children?: React.ReactNode;
}

const TOP_BAR_DEBOUNCE_MS = 150;

/**
 * Returns true after `delayMs` while `active` is true. Prevents top-bar flicker
 * on fast loads (< 150ms). Skeleton scaffold renders unconditionally — visual
 * continuity is more important than flicker for skeletons (per spec).
 */
function useDebouncedFlag(active: boolean, delayMs: number): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!active) {
      setShown(false);
      return;
    }
    const t = setTimeout(() => setShown(true), delayMs);
    return () => clearTimeout(t);
  }, [active, delayMs]);
  return shown;
}

const TopProgressBar: React.FC = () => (
  <LinearProgress
    sx={(theme) => ({
      // Always pin the page-loading strip to the bottom edge of the sticky
      // navbar, regardless of where the page renders this shared primitive.
      position: "fixed",
      top: { xs: 56, sm: 64 },
      left: 0,
      right: 0,
      height: 3,
      borderRadius: 0,
      zIndex: theme.zIndex.appBar + 1,
      pointerEvents: "none",
      bgcolor: "rgba(99, 102, 241, 0.16)",
      "& .MuiLinearProgress-bar": {
        background:
          "linear-gradient(90deg, #8B5CF6 0%, #6366F1 48%, #22D3EE 100%)",
      },
    })}
  />
);

/**
 * `<LoadingTopBar>` — standalone top-bar export for pages that need to position
 * the header progress separately from the body scaffold (e.g. PropertiesPage,
 * AdminDashboard). Same 150ms debounce as the full primitive.
 */
export const LoadingTopBar: React.FC<{
  loading: boolean;
  refreshing?: boolean;
}> = ({ loading, refreshing = false }) => {
  const show = useDebouncedFlag(loading || refreshing, TOP_BAR_DEBOUNCE_MS);
  return show ? <TopProgressBar /> : null;
};

/* -----------------------------------------------------------------------
 * Variant scaffolds
 * --------------------------------------------------------------------- */

const CardGridScaffold: React.FC<{ rows: number }> = ({ rows }) => (
  <Grid container spacing={3}>
    {Array.from({ length: rows }).map((_, i) => (
      <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
        <Card>
          <Skeleton animation="wave" variant="rectangular" height={160} />
          <CardContent>
            <Skeleton animation="wave" variant="text" width="80%" />
            <Skeleton animation="wave" variant="text" width="60%" />
            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <Skeleton
                animation="wave"
                variant="rounded"
                width={80}
                height={24}
              />
              <Skeleton
                animation="wave"
                variant="rounded"
                width={60}
                height={24}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

const ListScaffold: React.FC<{ rows: number }> = ({ rows }) => (
  <Box>
    {Array.from({ length: rows }).map((_, i) => (
      <Card key={i} sx={{ p: 2, mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Skeleton
            animation="wave"
            variant="rounded"
            width={40}
            height={40}
          />
          <Box sx={{ flex: 1 }}>
            <Skeleton animation="wave" variant="text" width="60%" height={18} />
            <Skeleton
              animation="wave"
              variant="text"
              width="90%"
              height={14}
              sx={{ mt: 0.5 }}
            />
          </Box>
          <Skeleton
            animation="wave"
            variant="rounded"
            width={50}
            height={20}
          />
        </Box>
      </Card>
    ))}
  </Box>
);

const DashboardScaffold: React.FC = () => (
  <Box>
    <Grid container spacing={1} sx={{ mb: 3 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Grid item xs={12} sm={3} key={i}>
          <Card sx={{ p: 2 }}>
            <Skeleton animation="wave" variant="text" width="60%" height={16} />
            <Skeleton
              animation="wave"
              variant="text"
              width="40%"
              height={32}
              sx={{ mt: 1 }}
            />
          </Card>
        </Grid>
      ))}
    </Grid>
    <Card sx={{ p: 2, mb: 3 }}>
      <Skeleton
        animation="wave"
        variant="rectangular"
        height={300}
        sx={{ borderRadius: 1 }}
      />
    </Card>
    <Card sx={{ p: 2 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            py: 1.25,
            borderBottom: i < 4 ? "1px solid" : "none",
            borderColor: "divider",
          }}
        >
          <Skeleton animation="wave" variant="text" width="20%" />
          <Skeleton animation="wave" variant="text" width="35%" />
          <Skeleton animation="wave" variant="text" width="15%" />
          <Skeleton
            animation="wave"
            variant="rounded"
            width={60}
            height={22}
            sx={{ ml: "auto" }}
          />
        </Box>
      ))}
    </Card>
  </Box>
);

const DetailScaffold: React.FC = () => (
  <Box>
    <Skeleton
      animation="wave"
      variant="rectangular"
      height={120}
      sx={{ borderRadius: 1, mb: 3 }}
    />
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} md={6}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton
            key={i}
            animation="wave"
            variant="text"
            width={i % 2 === 0 ? "70%" : "50%"}
            sx={{ mb: 0.75 }}
          />
        ))}
      </Grid>
      <Grid item xs={12} md={6}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton
            key={i}
            animation="wave"
            variant="text"
            width={i % 2 === 0 ? "60%" : "80%"}
            sx={{ mb: 0.75 }}
          />
        ))}
      </Grid>
    </Grid>
    <Card sx={{ p: 3 }}>
      {["95%", "88%", "92%", "75%", "85%", "60%"].map((w, i) => (
        <Skeleton
          key={i}
          animation="wave"
          variant="text"
          width={w}
          sx={{ mb: 0.75 }}
        />
      ))}
    </Card>
  </Box>
);

/* -----------------------------------------------------------------------
 * Main component
 * --------------------------------------------------------------------- */

const PageLoadingScaffold: React.FC<PageLoadingScaffoldProps> = ({
  loading,
  refreshing = false,
  variant = "card-grid",
  rows = 6,
  showTopBar: renderTopBar = true,
  children,
}) => {
  const topBarVisible = useDebouncedFlag(
    loading || refreshing,
    TOP_BAR_DEBOUNCE_MS,
  );

  const renderScaffold = () => {
    switch (variant) {
      case "list":
        return <ListScaffold rows={rows} />;
      case "dashboard":
        return <DashboardScaffold />;
      case "detail":
        return <DetailScaffold />;
      case "card-grid":
      default:
        return <CardGridScaffold rows={rows} />;
    }
  };

  return (
    <>
      {renderTopBar && topBarVisible && <TopProgressBar />}
      {loading ? renderScaffold() : children}
    </>
  );
};

export default PageLoadingScaffold;
